import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useBranding } from '../Context/BrandingContext';
import { getInventoriesByProduct, getProduct } from '../api/products';
import AppHeader from './AppHeader';

function normalizeArray(maybeArray) {
  if (Array.isArray(maybeArray)) return maybeArray;
  if (maybeArray && Array.isArray(maybeArray.data)) return maybeArray.data;
  if (maybeArray && Array.isArray(maybeArray.items)) return maybeArray.items;
  return [];
}

function getCartKey() {
  return 'avsight_marketplace_cart';
}

function readCart() {
  try {
    const raw = localStorage.getItem(getCartKey());
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeCart(items) {
  localStorage.setItem(getCartKey(), JSON.stringify(items));
}

export default function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { branding } = useBranding();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [product, setProduct] = useState(null);
  const [inventories, setInventories] = useState([]);
  const [qtyByInventoryId, setQtyByInventoryId] = useState({});
  const [cartCount, setCartCount] = useState(() => readCart().length);

  const title = useMemo(() => product?.Name || product?.name || 'Product', [product]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!productId) return;
      setLoading(true);
      setError('');
      try {
        const [p, inv] = await Promise.all([
          getProduct(productId),
          getInventoriesByProduct(productId, { moq: 0 }),
        ]);
        if (cancelled) return;
        setProduct(p || null);
        setInventories(normalizeArray(inv));
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load product');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [productId]);

  const handleAddToCart = (inv) => {
    const id = inv?.Id || inv?.id;
    if (!id) return;
    const qty = Math.max(1, Number(qtyByInventoryId[id] || 1));

    const cart = readCart();
    const existingIdx = cart.findIndex((c) => c.inventoryId === id);
    const nextItem = {
      inventoryId: id,
      productId: productId,
      productName: product?.Name || '',
      inventoryName: inv?.Name || '',
      condition: inv?.inscor__Condition_Code__c?.Name || '',
      quantity: qty,
      quantityAvailable: inv?.inscor__Quantity_Available__c,
    };
    if (existingIdx >= 0) {
      cart[existingIdx] = { ...cart[existingIdx], quantity: cart[existingIdx].quantity + qty };
    } else {
      cart.push(nextItem);
    }
    writeCart(cart);
    setCartCount(cart.length);
  };

  return (
    <div style={styles.page}>
      <AppHeader title={title} />

      <main style={styles.main}>
        <div style={styles.container}>
          {loading ? <div style={styles.muted}>Loading…</div> : null}
          {error ? <div style={styles.error}>{error}</div> : null}

          {product && (
            <div style={styles.card}>
              <div style={styles.productName}>{product?.Name}</div>
              {product?.inscor__Description__c ? (
                <div style={styles.productDesc}>{product.inscor__Description__c}</div>
              ) : (
                <div style={styles.muted}>No description</div>
              )}
              {product?.inscor__Keyword__c ? <div style={styles.badge}>{product.inscor__Keyword__c}</div> : null}
            </div>
          )}

          <div style={{ ...styles.card, marginTop: 14 }}>
            <div style={styles.sectionTitle}>Inventory</div>
            {inventories.length === 0 ? (
              <div style={styles.muted}>No inventory found for this product.</div>
            ) : (
              <div style={styles.inventoryList}>
                {inventories.map((inv) => {
                  const id = inv?.Id || inv?.id;
                  const invName = inv?.Name || '';
                  const cond = inv?.inscor__Condition_Code__c?.Name || '';
                  const qtyAvail = inv?.inscor__Quantity_Available__c;
                  return (
                    <div key={id || invName} style={styles.inventoryRow}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={styles.inventoryName}>{invName || id}</div>
                        <div style={styles.inventoryMeta}>
                          {cond ? `Condition: ${cond}` : 'Condition: —'} · Available: {qtyAvail ?? '—'}
                        </div>
                      </div>
                      <div style={styles.inventoryActions}>
                        <input
                          type="number"
                          min={1}
                          value={qtyByInventoryId[id] || 1}
                          onChange={(e) => setQtyByInventoryId((prev) => ({ ...prev, [id]: e.target.value }))}
                          style={styles.qtyInput}
                          aria-label="Quantity"
                        />
                        <button type="button" style={styles.secondaryButton} onClick={() => handleAddToCart(inv)}>
                          Add
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', backgroundColor: 'var(--background)', color: 'var(--foreground)' },
  main: { padding: '28px 24px' },
  container: { maxWidth: 1100, margin: '0 auto' },
  card: {
    backgroundColor: 'var(--card)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: 16,
  },
  productName: { fontSize: 18, fontWeight: 800 },
  productDesc: { marginTop: 8, color: 'var(--foreground)', lineHeight: 1.5 },
  muted: { color: 'var(--muted-foreground)' },
  badge: {
    display: 'inline-block',
    marginTop: 10,
    padding: '6px 10px',
    borderRadius: 999,
    backgroundColor: 'var(--muted)',
    border: '1px solid var(--border)',
    color: 'var(--foreground)',
    fontWeight: 700,
    fontSize: 12,
  },
  sectionTitle: { fontWeight: 800, marginBottom: 10 },
  inventoryList: { display: 'flex', flexDirection: 'column', gap: 10 },
  inventoryRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: 12,
    borderRadius: 10,
    border: '1px solid var(--border)',
    backgroundColor: 'var(--card)',
  },
  inventoryName: { fontWeight: 800, fontSize: 13 },
  inventoryMeta: { color: 'var(--muted-foreground)', fontSize: 12, marginTop: 4 },
  inventoryActions: { display: 'flex', gap: 8, alignItems: 'center' },
  qtyInput: {
    width: 70,
    height: 36,
    borderRadius: 8,
    border: '1px solid var(--border)',
    padding: '0 10px',
    backgroundColor: 'var(--input-background)',
  },
  secondaryButton: {
    height: 36,
    borderRadius: 8,
    border: '1px solid var(--border)',
    backgroundColor: 'var(--secondary)',
    color: 'var(--secondary-foreground)',
    fontWeight: 800,
    padding: '0 12px',
    cursor: 'pointer',
  },
  error: {
    marginBottom: 12,
    padding: '10px 12px',
    borderRadius: 8,
    backgroundColor: 'rgba(220, 38, 38, 0.08)',
    border: '1px solid rgba(220, 38, 38, 0.25)',
    color: 'var(--destructive)',
  },
};

